import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { humanizeSupabaseError } from "@/lib/supabase-errors";
import type { WorkspaceTask } from "@/hooks/useWorkspaceTasks";

const ORPHAN_SELECT = `id, workspace_id, title, description, due_date, due_at, status, outcome,
  completed_at, assigned_member_id, created_by_member_id, updated_at, priority, lead_id, tags,
  project_id, project_column_id, board_order_index,
  assignee:assigned_member_id(display_name),
  creator:created_by_member_id(display_name),
  lead:lead_id(id, name),
  subtasks:workspace_tasks!parent_task_id(id, status)`;

interface UseOrphanTasksArgs {
  workspaceId: string | null;
  memberId?: string | null;
  isAdmin?: boolean;
  onlyMine?: boolean;
  searchQuery?: string;
  enabled?: boolean;
}

export function useOrphanTasks({
  workspaceId,
  memberId,
  isAdmin = false,
  onlyMine = false,
  searchQuery = "",
  enabled = true,
}: UseOrphanTasksArgs) {
  const [orphans, setOrphans] = useState<WorkspaceTask[]>([]);
  const [orphanCount, setOrphanCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const fetchOrphans = useCallback(async () => {
    if (!workspaceId || !enabled) {
      setOrphans([]);
      setOrphanCount(0);
      return;
    }

    setLoading(true);
    try {
      const trimmedSearch = searchQuery.trim();

      let countQuery = supabase
        .from("workspace_tasks")
        .select("id", { count: "exact", head: true })
        .eq("workspace_id", workspaceId)
        .is("parent_task_id", null)
        .is("project_id", null)
        .neq("status", "cancelled");

      let listQuery = supabase
        .from("workspace_tasks")
        .select(ORPHAN_SELECT)
        .eq("workspace_id", workspaceId)
        .is("parent_task_id", null)
        .is("project_id", null)
        .neq("status", "cancelled")
        .order("due_date", { ascending: true, nullsFirst: false })
        .order("updated_at", { ascending: false })
        .limit(100);

      if ((!isAdmin || onlyMine) && memberId) {
        countQuery = countQuery.eq("assigned_member_id", memberId);
        listQuery = listQuery.eq("assigned_member_id", memberId);
      }

      if (trimmedSearch) {
        const pattern = `%${trimmedSearch.replace(/[%_\\]/g, "\\$&")}%`;
        countQuery = countQuery.ilike("title", pattern);
        listQuery = listQuery.ilike("title", pattern);
      }

      const [{ count, error: countError }, { data, error: listError }] = await Promise.all([
        countQuery,
        listQuery,
      ]);

      if (countError) throw countError;
      if (listError) throw listError;

      setOrphanCount(count ?? 0);
      setOrphans((data ?? []) as unknown as WorkspaceTask[]);
    } catch (err: unknown) {
      console.error("useOrphanTasks.fetchOrphans:", err);
      toast({
        title: "Erro ao carregar inbox",
        description: humanizeSupabaseError(err, {
          fallback: "Não foi possível carregar o inbox. Tente novamente.",
        }),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [workspaceId, memberId, isAdmin, onlyMine, searchQuery, enabled, toast]);

  useEffect(() => {
    void fetchOrphans();
  }, [fetchOrphans]);

  return {
    orphans,
    orphanCount,
    loading,
    refetch: fetchOrphans,
  };
}
